import type { MapLocation } from '../types';
import { PICKUP_COLOR, DELIVERY_COLOR, FLAG_PATH } from '../constants';

// TYPES --------------------------------------------------------------------- //

interface StopOverlayResult {
  geofences: (google.maps.Circle | google.maps.Polygon)[];
  pickupMarker: google.maps.Marker | null;
  deliveryMarker: google.maps.Marker | null;
}

export interface ClickedStop {
  type: 'pickup' | 'delivery';
  location: MapLocation;
  screenX: number;
  screenY: number;
  consumerName?: string;
  consumerPhone?: string;
  consumerAtStop?: boolean;
}

interface ConsumerContext {
  consumerName?: string;
  consumerPhone?: string;
  /** 1 = consumer at pickup, 0 = consumer at delivery */
  consumerAtPickup?: number | null;
}

interface CreateStopOverlaysOptions {
  map: google.maps.Map;
  pickup?: MapLocation;
  delivery?: MapLocation;
  renderedLocationIds: Set<number>;
  consumer?: ConsumerContext;
  onClickStop?: (stop: ClickedStop) => void;
  clickListeners?: google.maps.MapsEventListener[];
}

// HELPERS ------------------------------------------------------------------- //

function createGeofence(
  map: google.maps.Map,
  location: MapLocation,
  color: string,
  renderedIds: Set<number>,
  geofences: (google.maps.Circle | google.maps.Polygon)[]
): void {
  if (location.latitude == null || location.longitude == null || renderedIds.has(location.id)) return;
  renderedIds.add(location.id);

  const center = {
    lat: location.geofence_center_lat ?? location.latitude,
    lng: location.geofence_center_lng ?? location.longitude,
  };

  if (location.geofence_vertices && location.geofence_vertices.length >= 3) {
    geofences.push(new google.maps.Polygon({
      map,
      paths: location.geofence_vertices,
      fillColor: color,
      fillOpacity: 0.25,
      strokeColor: color,
      strokeOpacity: 0.9,
      strokeWeight: 2,
      clickable: false,
      zIndex: 2,
    }));
  } else {
    geofences.push(new google.maps.Circle({
      map,
      center,
      radius: location.geofence_radius ?? 200,
      fillColor: color,
      fillOpacity: 0.25,
      strokeColor: color,
      strokeOpacity: 0.9,
      strokeWeight: 2,
      clickable: false,
      zIndex: 2,
    }));
  }
}

function createFlagMarker(
  map: google.maps.Map,
  location: MapLocation,
  color: string,
  stopType: 'pickup' | 'delivery',
  onClickStop?: (stop: ClickedStop) => void,
  clickListeners?: google.maps.MapsEventListener[],
  consumer?: ConsumerContext,
): google.maps.Marker | null {
  if (location.latitude == null || location.longitude == null) return null;
  const marker = new google.maps.Marker({
    map,
    position: { lat: location.latitude, lng: location.longitude },
    icon: {
      path: FLAG_PATH,
      fillColor: color,
      fillOpacity: 1,
      strokeColor: '#ffffff',
      strokeWeight: 1.5,
      scale: 1.2,
      anchor: new google.maps.Point(6, 21),
    },
    clickable: true,
    cursor: 'pointer',
    zIndex: 20,
  });

  if (onClickStop) {
    // Determine if consumer is at this stop
    const consumerAtStop =
      consumer?.consumerName != null &&
      ((stopType === 'pickup' && consumer.consumerAtPickup === 1) ||
       (stopType === 'delivery' && (consumer.consumerAtPickup === 0 || consumer.consumerAtPickup == null)));

    const listener = marker.addListener('click', (e: google.maps.MapMouseEvent) => {
      const pixel = e.domEvent as MouseEvent;
      onClickStop({
        type: stopType,
        location,
        screenX: pixel.clientX,
        screenY: pixel.clientY,
        ...(consumerAtStop && {
          consumerName: consumer!.consumerName,
          consumerPhone: consumer!.consumerPhone,
        }),
      });
    });
    clickListeners?.push(listener);
  }

  return marker;
}

// FACTORY ------------------------------------------------------------------- //

/**
 * Creates geofence shapes + flag markers at pickup/delivery locations.
 * Deduplicates by location ID to avoid overlapping geofences.
 */
export function createStopOverlays({
  map,
  pickup,
  delivery,
  renderedLocationIds,
  consumer,
  onClickStop,
  clickListeners,
}: CreateStopOverlaysOptions): StopOverlayResult {
  const geofences: (google.maps.Circle | google.maps.Polygon)[] = [];
  let pickupMarker: google.maps.Marker | null = null;
  let deliveryMarker: google.maps.Marker | null = null;

  if (pickup) {
    createGeofence(map, pickup, PICKUP_COLOR, renderedLocationIds, geofences);
    pickupMarker = createFlagMarker(map, pickup, PICKUP_COLOR, 'pickup', onClickStop, clickListeners, consumer);
  }
  if (delivery) {
    createGeofence(map, delivery, DELIVERY_COLOR, renderedLocationIds, geofences);
    deliveryMarker = createFlagMarker(map, delivery, DELIVERY_COLOR, 'delivery', onClickStop, clickListeners, consumer);
  }

  return { geofences, pickupMarker, deliveryMarker };
}
